import styled, { keyframes } from "styled-components";
import Video from "../Video/Video";
import Image from "../Image/Image";

/* ─── Main card (the water pane) ─────────────────────────────────────────── */

export const ItemCard = styled.div`
  position: relative;
  border-radius: 26px 30px 28px 32px / 30px 28px 32px 28px;
  cursor: pointer;
  isolation: isolate;
  /* Clips bottom gradient + any overlay to the card's organic shape */
  overflow: hidden;
  transition: transform 280ms var(--ease-snappy);
  /* Soft water surface so the card isn't transparent before media loads. */
  background:
    radial-gradient(
      120% 90% at 20% 0%,
      rgba(var(--glass-white-rgb), 0.85) 0%,
      rgba(var(--glass-pale-rgb), 0.7) 45%,
      rgba(var(--glass-pale-rgb), 0.55) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.7) 0%,
      rgba(var(--glass-pale-rgb), 0.55) 100%
    );

  [data-theme="dark"] & {
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.45),
      0 1px 3px rgba(0, 0, 0, 0.3);
  }

  &:hover {
    .thumbnail {
      transform: rotate(0deg);
    }
  }
`;

/* ─── Inner frost highlight — bright top-of-water glow inside the rim ────
   Same visual feel as a soft blur, but achieved with extra alpha-stops in
   the gradient (cheap to render). No filter/mix-blend — those force a
   compositing layer per card and choke mobile GPU at scroll. */

export const FrostHighlight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 18px;
  width: 100%;
  border-radius: 999px;
  pointer-events: none;
  z-index: 3;
  background: linear-gradient(
    180deg,
    rgba(var(--glass-white-rgb), 0.55) 0%,
    rgba(var(--glass-white-rgb), 0.42) 18%,
    rgba(var(--glass-white-rgb), 0.26) 42%,
    rgba(255, 255, 255, 0.12) 68%,
    rgba(255, 255, 255, 0) 100%
  );

  [data-theme="dark"] & {
    opacity: 0;
  }
`;

/* ─── Media wrappers (clipped to card's organic radius) ─────────────────── */

export const StyledVideo = styled(Video)`
  position: relative;
  z-index: 2;
  aspect-ratio: unset;
  height: 100%;
  width: 100%;
  border-radius: inherit;
  overflow: hidden;
  cursor: pointer;
  video {
    border-radius: inherit;
  }
`;

export const StyledImage = styled(Image)`
  position: relative;
  z-index: 2;
  aspect-ratio: unset;
  height: 100%;
  width: 100%;
  border-radius: inherit;
  overflow: hidden;

  cursor: pointer;
  img {
    border-radius: inherit;
  }
`;

/* ─── Bottom area — title + stats row, sits on a soft gradient fade ────── */

export const BottomArea = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 8;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  align-items: center;
  gap: 8px;
  padding: 16px;
  pointer-events: none;
`;

/* Fixed-height bottom fade. Sits behind BottomArea so the gradient is
   stable while the title row swaps with the Generate CTA on hover. */
export const BottomGradient = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 120px;
  z-index: 7;
  pointer-events: none;
  border-radius: inherit;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.7) 100%
  );
`;

/* Template name row — centered, above the stats. Lives inside TitleSlot
   absolutely positioned so it can fade out as the Generate CTA fades in.
   Inner span carries the line-clamp; outer flex centers it vertically
   inside the 42px slot. */
export const CardTitle = styled.div`
  inset: 0;
  width: 100%;
  display: flex;
  align-items: center;
  z-index: 1;
  opacity: ${(props) => (props.$show ? 1 : 0)};
  transition: opacity 180ms cubic-bezier(0.22, 1, 0.36, 1);

  > span {
    width: 100%;
    text-align: left;
    color: var(--glass-text-on-pill);
    font-family: var(--font-family-primary);
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Stats row — light frosted pill anchored to the top-right corner.
   Matches the design's frosted-glass pill language so it doesn't feel
   like a heavy dark badge over the media. */
export const StatsRow = styled.div`
  left: 0;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  opacity: 0.8;
  z-index: 2;
`;

/* Small input-image thumbnail anchored to the top-left corner.
   Always visible when the item has an input frame, signalling the
   source still that powered the generation. */
export const ThumbnailImageSmall = styled.img`
  position: absolute;
  top: ${(p) => (p.$double ? "80px" : "16px")};
  left: 16px;
  z-index: 8;
  width: 64px;
  aspect-ratio: 2 / 3;
  height: auto;
  display: block;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--glass-edge-soft);
  background: rgba(0, 0, 0, 0.28);
  box-shadow:
    0 6px 14px rgba(0, 0, 0, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
  user-select: none;
  pointer-events: none;
  transform: rotate(-5deg);
  transition: all 0.2s ease-in-out;
  @media (max-width: 768px) {
    width: 48px;
  }
  @media (max-width: 370px) {
    width: 40px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const StatItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: -0.005em;
  line-height: normal;

  svg {
    width: 13px;
    height: 13px;
    color: var(--glass-text-on-pill);
    flex-shrink: 0;
    stroke-width: 3px;
  }

  span {
    font-variant-numeric: tabular-nums;
  }
`;

/* ─── Badges (kept for special-tag cases) ───────────────────────────────── */

export const BadgeRow = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 25;
  display: flex;
  gap: 6px;
`;

export const Badge = styled.span`
  border-radius: var(--radius-pill);
  max-height: 22px;
  background: ${(props) => {
    if (props.$variant === "Free Trial") {
      return "linear-gradient(90deg, var(--color-brand-500) 0%, var(--color-brand-400) 100%)";
    } else if (props.$variant === "Popular") {
      return "linear-gradient(90deg, #ffb84d 0%, #ff8f5a 100%)";
    } else if (props.$variant === "New") {
      return "linear-gradient(90deg, #5ee2a4 0%, #2fb48d 100%)";
    } else if (props.$variant === "Trending") {
      return "linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 100%)";
    } else if (props.$variant === "Santa's Special") {
      return "linear-gradient(90deg, #c55d66 0%, #ff8f5a 100%)";
    } else if (props.$variant === "VIP") {
      return "linear-gradient(270deg, var(--color-brand-400) 0%, var(--color-brand-600) 100%)";
    } else if (props.$variant === "8s") {
      return "var(--color-brand-600)";
    }
    return "linear-gradient(90deg, var(--color-brand-500) 0%, var(--color-brand-600) 100%)";
  }};

  border: 1px solid
    ${(props) =>
      props.$variant === "New"
        ? "rgba(200, 244, 231, 1)"
        : props.$variant === "Free Trial"
          ? "rgba(148, 203, 255, 0.55)"
          : "rgba(var(--glass-white-rgb), 0.55)"};
  box-shadow: ${(props) =>
    props.$variant === "New"
      ? `0px 4px 10px 0px rgba(73, 205, 154, 0.28),
         inset 0px 1px 4px 0px rgba(255, 255, 255, 0.6)`
      : props.$variant === "Free Trial"
        ? `0px 4px 10px 0px rgba(27, 146, 252, 0.28),
           inset 0px 1px 8px 0px rgba(255, 255, 255, 0.6)`
        : `0 4px 10px rgba(var(--color-brand-500-rgb), 0.28),
         inset 0 1px 0 rgba(255, 255, 255, 0.6)`};

  display: flex;
  padding: 3px 10px;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 0 rgba(var(--color-brand-700-rgb), 0.3);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.03em;
  line-height: normal;
  white-space: nowrap;
  min-height: 22px;

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 2px 8px;
    min-height: 20px;
  }
  @media (max-width: 768px) {
    font-size: 8px;
    padding: 2px 4px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Hover affordances (Generate CTA etc.) ──────────────────────────────── */

/* HoverOverlay — DEPRECATED / no-op stub (kept for back-compat imports) */
export const HoverOverlay = styled.div`
  display: none;
`;

/* TitleSlot — fixed-height stage that hosts the title row and the Generate
   CTA stacked on top of each other. Both children stay mounted; visibility
   is driven by CSS opacity/transform transitions on the $show prop, so
   the swap is GPU-cheap and does not pull in framer-motion per card. */
export const TitleSlot = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

/* Container for the Generate CTA. Sits absolutely inside TitleSlot so it
   shares space with CardTitle. Visibility is driven by $show. */
export const GeneratePortal = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  border-radius: 0px 0px 28px 32px / 0px 0px 32px 28px;

  pointer-events: ${(props) => (props.$show ? "auto" : "none")};
  opacity: ${(props) => (props.$show ? 1 : 0)};
  transform: ${(props) =>
    props.$show ? "translateY(0) scale(1)" : "translateY(10px) scale(0.92)"};
  transition:
    opacity 260ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 160ms cubic-bezier(0.22, 1, 0.36, 1);

  background: linear-gradient(transparent 0%, black 100%);
`;

/* Shine sweep on hover of the Generate CTA */
const generateBtnShine = keyframes`
  0% {
    transform: translateX(-140%) skewX(-18deg);
    opacity: 0;
  }
  30% {
    opacity: 0.85;
  }
  100% {
    transform: translateX(260%) skewX(-18deg);
    opacity: 0;
  }
`;

/* Generate CTA — frosted water-glass pill with brand-gradient text.
   Static pill; show/hide is driven by the parent GeneratePortal's $show
   prop (CSS opacity/transform). No JS animation per card. */
export const GenerateButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 26px;
  height: 42px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--glass-white-rgb), 0.92);
  background:
    radial-gradient(
      120% 100% at 20% 0%,
      rgba(var(--glass-white-rgb), 0.98) 0%,
      rgba(var(--glass-pale-rgb), 0.88) 55%,
      rgba(var(--glass-pale-rgb), 0.8) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.92) 0%,
      rgba(var(--glass-pale-rgb), 0.84) 100%
    );
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow:
    0 14px 28px rgba(var(--color-brand-600-rgb), 0.38),
    0 3px 8px rgba(var(--color-brand-600-rgb), 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 1),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.28),
    inset 0 0 0 1px rgba(255, 255, 255, 0.5);

  [data-theme="dark"] & {
    /* Keep the frosted-white pill in dark theme — but background is hardcoded
       white-ish, brand text reads, and the bright brand glow is replaced with
       a calm dark drop shadow. No glass-edge outline. */
    border-color: transparent;
    background: radial-gradient(
      120% 100% at 20% 0%,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(232, 244, 255, 0.86) 55%,
      rgba(214, 234, 255, 0.78) 100%
    );
    box-shadow:
      0 8px 18px rgba(0, 0, 0, 0.5),
      inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.24);
  }
  cursor: pointer;
  outline: none;
  overflow: hidden;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  white-space: nowrap;
  transition:
    filter 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  /* Top sheen — bright reflection on the pill's upper edge */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 14px;
    right: 14px;
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Hover shine — diagonal bright sweep across the button face */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 40%;
    pointer-events: none;
    background: linear-gradient(
      100deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(var(--color-brand-400-rgb), 0.32) 45%,
      rgba(var(--color-brand-400-rgb), 0.6) 50%,
      rgba(var(--color-brand-400-rgb), 0.32) 55%,
      rgba(255, 255, 255, 0) 100%
    );
    mix-blend-mode: overlay;
    transform: translateX(-140%) skewX(-18deg);
    opacity: 0;
  }

  /* Gradient-clipped text — lives on the inner span */
  > span {
    position: relative;
    z-index: 1;
    background: linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  &:hover {
    filter: brightness(1.04);
    box-shadow:
      0 18px 34px rgba(var(--color-brand-600-rgb), 0.48),
      0 4px 10px rgba(var(--color-brand-600-rgb), 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 1),
      inset 0 -3px 10px rgba(var(--color-brand-200-rgb), 0.4),
      inset 0 0 0 1px rgba(255, 255, 255, 0.7);
  }

  [data-theme="dark"] &:hover {
    box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.6),
      inset 0 -3px 10px rgba(var(--color-brand-200-rgb), 0.32);
  }

  &:hover::after {
    animation: ${generateBtnShine} 900ms ease-out;
  }

  @media (max-width: 768px) {
    height: 38px;
    padding: 0 20px;
    font-size: 13px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Structured placeholder skeleton ──────────────────────────────────────
   Mirrors the real card layout: rotated thumbnail (top-left), badge pill
   (top-right), two title lines + stats row at the bottom. The overlay
   itself stays transparent so the card's own water gradient reads through
   the gaps; only the inner boxes change shade. No shimmer sweep. */

const skeletonShade = keyframes`
  0%, 100% {
    background: rgba(var(--glass-pale-rgb), 0.55);
  }
  50% {
    background: rgba(var(--glass-pale-rgb), 0.85);
  }
`;

export const SkeletonOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 9;
  pointer-events: none;
`;

export const SkeletonThumbnailPlaceholder = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 64px;
  aspect-ratio: 2 / 3;
  border-radius: 8px;
  border: 1px solid var(--glass-edge-soft);
  transform: rotate(-5deg);
  animation: ${skeletonShade} 1.6s ease-in-out infinite;

  @media (max-width: 768px) {
    width: 48px;
  }
  @media (max-width: 370px) {
    width: 40px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const SkeletonBadgePlaceholder = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 60px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  animation: ${skeletonShade} 1.6s ease-in-out infinite;
  animation-delay: 80ms;

  @media (max-width: 768px) {
    width: 52px;
    height: 20px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const SkeletonTitleLine = styled.div`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 56px;
  height: 14px;
  border-radius: 6px;
  animation: ${skeletonShade} 1.6s ease-in-out infinite;
  animation-delay: 160ms;
`;

export const SkeletonTitleLineShort = styled.div`
  position: absolute;
  left: 16px;
  bottom: 38px;
  width: 60%;
  height: 14px;
  border-radius: 6px;
  animation: ${skeletonShade} 1.6s ease-in-out infinite;
  animation-delay: 240ms;
`;

export const SkeletonStatsRow = styled.div`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const SkeletonStatPlaceholder = styled.div`
  width: 36px;
  height: 12px;
  border-radius: 4px;
  animation: ${skeletonShade} 1.6s ease-in-out infinite;
  animation-delay: 320ms;
`;

/* ─── Legacy exports kept for back-compat (hidden/no-op now) ────────────── */

export const PlayBadge = styled.div`
  display: none;
`;

export const StatPill = styled.div`
  display: none;
`;

export const RateContainer = styled.div`
  display: none;
`;

export const RateNumber = styled.span`
  display: none;
`;

export const RateIcon = styled.span`
  display: none;
`;

export const Username = styled.div`
  display: none;
`;

export const ModelName = styled.h3`
  display: none;
`;

export const VideoFooter = styled.div`
  display: none;
`;

export const RunningText = styled.div`
  color: var(--color-text-strong);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .running-title {
    color: var(--color-text-strong);
    font-family: var(--font-family-primary);
    font-size: 18px;
    font-weight: 700;
  }

  .running-message {
    color: var(--color-text-strong);
    font-family: var(--font-family-primary);
    font-size: 14px;
    font-weight: 400;
  }
`;

export const LoadingSpinnerBlur = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: ${(props) => (props.$show ? "flex" : "none")};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;
  height: 100%;
  width: 100%;
  border-radius: inherit;
  background: rgba(var(--glass-pale-rgb), 0.82);
  border: 1px solid var(--color-line);
  backdrop-filter: blur(23px) saturate(160%);
  -webkit-backdrop-filter: blur(23px) saturate(160%);
  gap: 12px;
  color: var(--color-text-strong);

  l-bouncy {
    --spinner-color: var(--color-brand-600);
  }
`;
